import { useEffect, useState, type ReactNode } from "react";
import { Me, getAuth } from "@/lib/api";
import { Link, navigate } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

type State =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "forbidden"; roles: string[] }
  | { status: "ok"; me: any };

export function AdminGuard({ children }: { children: ReactNode }) {
  const [s, setS] = useState<State>({ status: "loading" });

  useEffect(() => {
    if (!getAuth()) { setS({ status: "anon" }); return; }
    Me.profile()
      .then((me: any) => {
        const roles: string[] = Array.isArray(me?.roles) ? me.roles : [];
        if (roles.includes("admin") || roles.includes("editor")) {
          setS({ status: "ok", me });
        } else {
          setS({ status: "forbidden", roles });
        }
      })
      .catch(() => setS({ status: "anon" }));
  }, []);

  if (s.status === "loading") {
    return <div className="container mx-auto py-24 text-center text-muted-foreground">校验权限中...</div>;
  }
  if (s.status === "anon") {
    // 未登录：跳转到登录页，登录成功后回到 admin
    if (typeof window !== "undefined") {
      const back = window.location.hash.slice(1) || "/admin";
      sessionStorage.setItem("post_login", back);
    }
    navigate("/login");
    return null;
  }
  if (s.status === "forbidden") {
    return (
      <div className="container mx-auto px-4 py-24 max-w-md text-center space-y-4">
        <ShieldAlert className="h-12 w-12 mx-auto text-destructive" />
        <h1 className="text-2xl font-bold">无权访问</h1>
        <p className="text-sm text-muted-foreground">
          当前账户角色：{s.roles.length ? s.roles.join(", ") : "（无）"}
          ，需要 <code>admin</code> 或 <code>editor</code> 权限。
        </p>
        <div className="flex gap-2 justify-center">
          <Link to="/"><Button variant="outline">返回首页</Button></Link>
          <Link to="/me"><Button>个人中心</Button></Link>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
